<script setup lang="ts">
import {Check, Minus, Pencil, Plus, Trash2, Trash2Icon} from "lucide-vue-next";
import {onMounted, type Ref, ref, watch} from "vue";
import {toast} from "vue-sonner";
import Input from "../../../backend/ui/Input.vue";
import type {CartCalculatorLineResponse} from "../../../responses/Site/CartCalculatorLineResponse.ts";
import {Money} from "../../../utils/money.js";
import {label} from "../../mixins/translations.ts";
import {useCartStore} from "../../stores/cart.js";
import CustomNumberInput from "../CustomNumberInput.vue";
import ImageWithFallback from "../ImageWithFallback.vue";
import CartPriceLoadingSkeleton from "./CartPriceLoadingSkeleton.vue";
import CartStock from "./CartStock.vue";

const cartStore = useCartStore();

interface Props {
    item: CartCalculatorLineResponse
    canEdit?: boolean
}

const props = withDefaults<Props>(defineProps<Props>(), {
    canEdit: true
})

const quantity: Ref<number> = ref(props.item.model.quantity);

const removeItem = async () => {
    await cartStore.destroyLine({variant_id: props.item.model.item.id})
        .then((response) => {
            toast.success(response.data.message)
        }).catch((_response) => {
            toast.error(label('site.cart.item.deleted.error'))
        })
}


const updateQuantity = async (quantity) => {
    if(quantity < 1) {
        removeItem();
    } else {
        await cartStore.updateQuantity({
            cart_item_id: props.item.model.id,
            quantity: quantity,
        }).then((response) => {
            toast.success(label('site.cart.item.update'))
        }).catch((response) => {
            toast.error(label('site.cart.item.update.error'))
        })
    }
}

const handleAmountChange = (newAmount) => {
    quantity.value = newAmount;
    updateQuantity(quantity.value);
}

</script>
<template>
    <section class="grid gap-5 border-b border-slate-200 pb-8 lg:grid-cols-[minmax(0,1fr)_auto] lg:gap-6">
        <div class="flex flex-col gap-4 sm:flex-row sm:gap-5">
            <div class="mx-auto flex h-28 w-24 shrink-0 items-start justify-center rounded-2xl bg-white">
                <a :href="item.model.item?.product?.url">
                    <ImageWithFallback
                        :src="item.model.item.product?.thumbnail?.image_url"
                        :alt="item.model.item.name"
                        class="max-h-full w-auto object-contain"
                    />
                </a>
            </div>

            <div class="min-w-0 flex-1">
                <div class="flex flex-col gap-4 lg:hidden">
                    <div class="flex items-start justify-between gap-3">
                        <a :href="item.model.item?.product?.url">
                            <h2 class="text-lg lg:text-2xl font-semibold leading-tight text-secondary-700">
                                {{ item.model.item.name }}
                            </h2>
                        </a>
                        <div class="shrink-0 text-right text-lg font-semibold text-slate-900">
                            {{ item.price_incl }}
                        </div>
                    </div>

                    <div class="flex items-center justify-end gap-2">
                        <button
                            type="button"
                            class="inline-flex h-9 w-9 items-center justify-center rounded-md bg-primary-50 text-primary-500 transition hover:bg-primary-100"
                            aria-label="Product bewerken"
                        >
                            <Pencil class="h-4 w-4" />
                        </button>
                        <button
                            type="button"
                            class="inline-flex h-9 w-9 items-center justify-center rounded-md bg-red-50 text-red-400 transition hover:bg-red-100"
                            aria-label="Product verwijderen"
                            @click.prevent="removeItem"
                        >
                            <Trash2 class="h-4 w-4" />
                        </button>
                    </div>
                </div>

                <div class="hidden lg:flex lg:items-start lg:justify-between lg:gap-6">
                    <a :href="`/${item.model.item?.product?.slug}/`">
                        <h2 class="text-xl font-semibold leading-tight text-secondary-800">
                            {{ item.model.item.name }}
                        </h2>
                    </a>
                </div>

    <!--            <p v-if="item.color" class="mt-2 text-sm text-secondary-600 sm:text-base">-->
    <!--                <span class="font-semibold text-secondary-700">Kleur:</span> {{ item.color }}-->
    <!--            </p>-->

    <!--            <p v-if="item.description" class="mt-3 max-w-3xl text-sm leading-6 text-secondary-600 sm:text-base">-->
    <!--                {{ item.description }}-->
    <!--            </p>-->

                <div class="mt-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-5">
                    <CustomNumberInput @input="handleAmountChange" :value="quantity" :min="1"></CustomNumberInput>

                    <cart-stock v-if="item.model.item.stock" :stock="item.model.item.stock"></cart-stock>
                </div>

    <!--            <div v-if="item.extraProduct" class="mt-6 max-w-md">-->
    <!--                <p class="mb-3 text-sm font-semibold text-secondary-700 sm:text-base">Maak dit product compleet</p>-->

    <!--                <div class="flex items-center justify-between gap-4 rounded-lg border border-slate-200 bg-white px-4 py-4">-->
    <!--                    <div class="flex min-w-0 items-start gap-5">-->
    <!--                        <div class="flex w-16 shrink-0 items-center justify-center rounded-full border border-secondary-100 bg-stone-50">-->
    <!--                            <img :src="item.extraProduct.image" :alt="item.title" class="max-h-full w-auto object-contain" />-->
    <!--                        </div>-->
    <!--                        <div class="min-w-0">-->
    <!--                            <p class="text-base leading-5 text-slate-700 leading-relaxed">-->
    <!--                                {{ item.extraProduct.title }}-->
    <!--                            </p>-->
    <!--                            <p class="mt-2 text-sm font-semibold leading-relaxed text-slate-900">-->
    <!--                                {{ item.extraProduct.price }}-->
    <!--                            </p>-->
    <!--                        </div>-->
    <!--                    </div>-->
    <!--                    <button-->
    <!--                        type="button"-->
    <!--                        class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-slate-700 transition hover:bg-secondary-50"-->
    <!--                        aria-label="Extra product toevoegen"-->
    <!--                    >-->
    <!--                        <Plus class="h-5 w-5" />-->
    <!--                    </button>-->
    <!--                </div>-->
    <!--            </div>-->
            </div>
        </div>

        <div class="hidden lg:flex lg:min-w-44 lg:flex-col lg:items-end lg:justify-start lg:gap-3">
            <div class="flex items-center gap-2">
                <button
                    type="button"
                    class="inline-flex h-9 w-9 items-center justify-center rounded-md bg-secondary-50 text-primary-500 transition hover:bg-slate-100"
                    aria-label="Product bewerken"
                >
                    <Pencil class="h-4 w-4" />
                </button>
                <button
                    type="button"
                    class="inline-flex h-9 w-9 items-center justify-center rounded-md bg-red-50 text-red-400 transition hover:bg-red-100"
                    aria-label="Product verwijderen"
                    @click.prevent="removeItem"
                >
                    <Trash2 class="h-4 w-4" />
                </button>

                <div class="text-right text-xl font-semibold text-slate-900 ml-4">
                    <CartPriceLoadingSkeleton>{{ Money.format(item.price_incl, item.currency) }}</CartPriceLoadingSkeleton>
                </div>
            </div>
        </div>
    </section>
</template>
